// ─── Thesis Export Validator ─────────────────────────────────────────────────
// Pre-export validation gate. Must pass before any DOCX/HTML export proceeds.
// Returns { passed, errors, warnings, exportReadinessScore, details }

import { supabase } from '../lib/supabase'
import { findContentPlaceholders, findBrokenSentences, findFrontMatterPlaceholders } from './contentSanitizer'
import { buildNumberingRegistries, findDuplicateNumbers, registryStats } from './numberingEngine'

// ── Helpers ───────────────────────────────────────────────────────────────────

function stripHtml(html) {
  return (html || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
}

function hasReferencesSection(chapters) {
  for (const ch of chapters) {
    const text = stripHtml(ch.content || '').toLowerCase()
    if (text.includes('references') || text.includes('bibliography')) return true
    if ((ch.title || '').toLowerCase().includes('references')) return true
  }
  return false
}

function hasMermaidLeakage(html) {
  return /<pre[^>]*class="mermaid"[^>]*>/i.test(html || '')
}

function hasRawMarkdownLeakage(html) {
  // Check for markdown headings or fences that weren't converted
  const text = html || ''
  return /^#{1,4}\s/m.test(text) || /```\w*/m.test(text)
}

function detectDuplicateIds(chapters) {
  const seen = new Map() // id → chapter
  const dups = []
  for (const ch of chapters) {
    const html = ch.content || ''
    const ids  = [...html.matchAll(/\bid="([^"]+)"/g)].map(m => m[1])
    for (const id of ids) {
      if (seen.has(id)) {
        dups.push({ id, chapters: [seen.get(id), ch.chapter_number] })
      } else {
        seen.set(id, ch.chapter_number)
      }
    }
  }
  return dups
}

// ── Front matter completeness ─────────────────────────────────────────────────

const REQUIRED_FM_FIELDS = [
  { key: 'student_name',     label: 'Student Name' },
  { key: 'supervisor_name',  label: 'Supervisor Name' },
  { key: 'university_name',  label: 'University Name' },
  { key: 'faculty_name',     label: 'Department/Faculty' },
  { key: 'degree',           label: 'Degree' },
  { key: 'submission_year',  label: 'Submission Year' },
  { key: 'abstract_text',    label: 'Abstract' },
]

function checkFrontMatterCompleteness(fm) {
  const missing = []
  if (!fm) return REQUIRED_FM_FIELDS.map(f => f.label)
  for (const { key, label } of REQUIRED_FM_FIELDS) {
    if (!fm[key] || String(fm[key]).trim() === '') missing.push(label)
  }
  return missing
}

// ── Main validator ────────────────────────────────────────────────────────────

/**
 * Run all export validation checks for a thesis.
 *
 * @param {string} thesisId
 * @param {{ thesis, chapters, fm, figures }} [preloaded]  - pass if already fetched
 * @returns {Promise<ValidationResult>}
 */
export async function validateFinalThesis(thesisId, preloaded = null) {
  const errors   = []
  const warnings = []

  // ── 1. Load data ──────────────────────────────────────────────────────────
  let thesis, chapters, fm, dbFigures

  if (preloaded) {
    thesis     = preloaded.thesis
    chapters   = preloaded.chapters
    fm         = preloaded.fm
    dbFigures  = preloaded.figures || []
  } else {
    const [tRes, cRes, fRes, figRes] = await Promise.all([
      supabase.from('theses').select('*').eq('id', thesisId).maybeSingle(),
      supabase.from('chapters').select('*').eq('thesis_id', thesisId).order('chapter_number'),
      supabase.from('thesis_front_matter').select('*').eq('thesis_id', thesisId).maybeSingle(),
      supabase.from('thesis_figures').select('*').eq('thesis_id', thesisId).order('display_order'),
    ])
    thesis    = tRes.data
    chapters  = cRes.data || []
    fm        = fRes.data
    dbFigures = figRes.data || []
  }

  // ── 2. Front matter checks ────────────────────────────────────────────────
  const missingFm = checkFrontMatterCompleteness(fm)
  if (missingFm.length > 0) {
    errors.push({
      code:     'FM_MISSING_FIELDS',
      severity: 'error',
      message:  `Front matter missing: ${missingFm.join(', ')}`,
      missing:  missingFm,
      repair:   { label: 'Complete Front Matter', href: `/app/thesis/${thesisId}/front-matter` },
    })
  }

  // Check front matter for un-interpolated placeholders
  const fmText = JSON.stringify(fm || {})
  const fmPlaceholders = findFrontMatterPlaceholders(fmText)
  if (fmPlaceholders.length > 0) {
    errors.push({
      code:     'FM_PLACEHOLDERS',
      severity: 'error',
      message:  `Front matter contains unfilled placeholders: ${[...new Set(fmPlaceholders)].join(', ')}`,
      repair:   { label: 'Fix Front Matter', href: `/app/thesis/${thesisId}/front-matter` },
    })
  }

  // ── 3. Chapter-level checks ───────────────────────────────────────────────
  if (chapters.length === 0) {
    errors.push({
      code:     'NO_CHAPTERS',
      severity: 'error',
      message:  'No chapters found. Generate at least one chapter before exporting.',
      repair:   { label: 'Go to Chapters', href: `/app/thesis/${thesisId}/chapters` },
    })
  }

  for (const ch of chapters) {
    const chLabel = `Chapter ${ch.chapter_number} "${ch.title}"`
    const html    = ch.content || ''

    // Empty chapter
    if (!html.trim() || stripHtml(html).length < 100) {
      warnings.push({
        code:      'EMPTY_CHAPTER',
        severity:  'warning',
        message:   `${chLabel} has little or no content.`,
        chapterId: ch.id,
        repair:    { label: `Generate ${chLabel}`, href: `/app/thesis/${thesisId}/chapters/${ch.id}` },
      })
      continue
    }

    // Placeholder tokens in content
    const phTokens = findContentPlaceholders(html)
    if (phTokens.length > 0) {
      errors.push({
        code:      'CONTENT_PLACEHOLDERS',
        severity:  'error',
        message:   `${chLabel} contains ${phTokens.length} placeholder(s): ${phTokens.map(p => p.token).join(', ')}`,
        chapterId: ch.id,
        details:   phTokens,
        repair:    { label: `Edit ${chLabel}`, href: `/app/thesis/${thesisId}/chapters/${ch.id}` },
      })
    }

    // Broken sentences
    const broken = findBrokenSentences(html)
    if (broken.length > 0) {
      warnings.push({
        code:      'BROKEN_SENTENCES',
        severity:  'warning',
        message:   `${chLabel} has ${broken.length} potentially incomplete sentence(s).`,
        chapterId: ch.id,
        details:   broken,
        repair:    { label: `Edit ${chLabel}`, href: `/app/thesis/${thesisId}/chapters/${ch.id}` },
      })
    }

    // Raw mermaid leakage
    if (hasMermaidLeakage(html)) {
      errors.push({
        code:      'MERMAID_LEAKAGE',
        severity:  'error',
        message:   `${chLabel} contains raw Mermaid code that was not converted to a figure token.`,
        chapterId: ch.id,
        repair:    { label: `Regenerate ${chLabel}`, href: `/app/thesis/${thesisId}/chapters/${ch.id}` },
      })
    }

    // Markdown leakage in prose (## headings not converted, ``` fences)
    if (hasRawMarkdownLeakage(html)) {
      warnings.push({
        code:      'MARKDOWN_LEAKAGE',
        severity:  'warning',
        message:   `${chLabel} may contain unconverted Markdown syntax.`,
        chapterId: ch.id,
        repair:    { label: `Edit ${chLabel}`, href: `/app/thesis/${thesisId}/chapters/${ch.id}` },
      })
    }
  }

  // ── 4. Figure registry checks ─────────────────────────────────────────────
  const { figures, tables } = buildNumberingRegistries(chapters, dbFigures)
  const dupErrors = findDuplicateNumbers(figures, tables)
  for (const dup of dupErrors) {
    errors.push({
      code:     dup.type === 'duplicate_figure' ? 'DUPLICATE_FIGURE_NUMBER' : 'DUPLICATE_TABLE_NUMBER',
      severity: 'error',
      message:  `Duplicate ${dup.type === 'duplicate_figure' ? 'Figure' : 'Table'} ${dup.number} in tokens: ${dup.tokens.join(', ')}`,
    })
  }

  const { missingCaptions } = registryStats(figures, tables)
  if (missingCaptions.length > 0) {
    warnings.push({
      code:     'MISSING_CAPTIONS',
      severity: 'warning',
      message:  `${missingCaptions.length} figure(s)/table(s) have empty captions.`,
      details:  missingCaptions.map(f => f.displayLabel || f.token),
    })
  }

  // ── 5. Duplicate HTML IDs across chapters ─────────────────────────────────
  const dupIds = detectDuplicateIds(chapters)
  if (dupIds.length > 0) {
    warnings.push({
      code:     'DUPLICATE_IDS',
      severity: 'warning',
      message:  `${dupIds.length} duplicate HTML ID(s) found across chapters.`,
      details:  dupIds.map(d => d.id),
    })
  }

  // ── 6. References section check ───────────────────────────────────────────
  if (chapters.length > 0 && !hasReferencesSection(chapters)) {
    warnings.push({
      code:     'NO_REFERENCES',
      severity: 'warning',
      message:  'No References or Bibliography section found.',
      repair:   { label: 'Go to Chapters', href: `/app/thesis/${thesisId}/chapters` },
    })
  }

  // ── 7. Draft chapter warning ──────────────────────────────────────────────
  const draftChapters = chapters.filter(c => c.status === 'DRAFT' || c.status === 'NOT_STARTED')
  if (draftChapters.length > 0) {
    warnings.push({
      code:     'DRAFT_CHAPTERS',
      severity: 'warning',
      message:  `${draftChapters.length} chapter(s) are still in DRAFT/NOT_STARTED state. Export will include draft content.`,
      details:  draftChapters.map(c => `Chapter ${c.chapter_number}: ${c.title}`),
    })
  }

  // ── 8. Compute readiness score ────────────────────────────────────────────
  const totalChecks   = 10
  const errorPenalty  = errors.length * 12
  const warnPenalty   = warnings.length * 4
  const rawScore      = Math.max(0, 100 - errorPenalty - warnPenalty)
  const exportReadinessScore = Math.round(rawScore)

  const passed = errors.length === 0

  return {
    passed,
    errors,
    warnings,
    exportReadinessScore,
    details: {
      thesis,
      chapters,
      fm,
      figures,
      tables,
      figureCount:   figures.size,
      tableCount:    tables.size,
      chapterCount:  chapters.length,
      draftCount:    draftChapters.length,
    },
  }
}
